import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import ImageUpload from './ImageUpload';
import './Modal.css';

export default function ModalAnimal({ onClose, onCreado, animal = null }) {
  const { usuario, esAdmin, esMaestro } = useAuth();

  // Bloquear el scroll del documento mientras el modal está abierto y
  // restaurarlo al cerrar, conservando la posición exacta anterior.
  useEffect(() => {
    const prev = document.body.style.overflow;
    const prevPad = document.body.style.paddingRight;
    const hasScrollbar = window.innerWidth > document.documentElement.clientWidth;
    if (hasScrollbar) document.body.style.paddingRight = '15px';
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
      document.body.style.paddingRight = prevPad;
    };
  }, []);
  
  // Determinamos si el usuario puede editar:
  // 1. Es admin o maestro
  // 2. Es el creador del registro
  const idCreador = animal?.creadoPor?._id || animal?.creadoPor;
  const puedoEditar = esAdmin || esMaestro || (usuario && idCreador === usuario._id);
  // Si no hay animal es creación nueva, solo permitida si está logueado (esto se controla en el botón que abre el modal)
  const esCreacion = !animal;

  const [form, setForm] = useState({ 
    nombre:   animal?.nombre || '', 
    especie:  animal?.especie || 'perro', 
    edad:     animal?.edad || '', 
    tamano:   animal?.tamano || 'mediano', 
    estado:   animal?.estado || 'adopcion', 
    historia: animal?.historia || '', 
    salud:    animal?.salud || '', 
    ubicacion:animal?.ubicacion || '',
    sexo:     animal?.sexo || 'desconocido'
  });
  const [foto, setFoto] = useState(null);
  const [loading, setLoading] = useState(false);

  const handle = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async e => {
    e.preventDefault();
    if (!puedoEditar && !esCreacion) return;
    
    setLoading(true);
    const formData = new FormData();
    Object.keys(form).forEach(key => formData.append(key, form[key]));
    if (foto) formData.append('foto', foto);

    try {
      if (animal) {
        await api.put(`/animales/${animal._id}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success(`¡Datos de ${form.nombre} actualizados! 🐾`);
      } else {
        await api.post('/animales', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        if (esAdmin || esMaestro) {
          toast.success(`¡${form.nombre} agregado al catálogo! 🐾`);
        } else {
          toast.success(`Propuesta de ${form.nombre} enviada al administrador. ⏳ En revisión.`);
        }
      }
      if (onCreado) onCreado(); 
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.msg || 'Error al procesar');
    } finally { setLoading(false); }
  };

  return (
    <div className="overlay fade-in" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal card fade-up max-w-[640px]">
        <div className="modal-header">
          <h2 className="sec-title">
            {esCreacion ? '🐾 Agregar animal' : (puedoEditar ? `🐾 Editar ${animal.nombre}` : `🐾 Detalles de ${animal.nombre}`)}
          </h2>
          <button className="modal-close-btn" onClick={onClose}>✕</button>
        </div>
        
        <form onSubmit={submit} className="modal-form">
          <div className="modal-grid-2">
            <div className="modal-left">
              {puedoEditar || esCreacion ? (
                <ImageUpload 
                  label="Foto del animal" 
                  onFileSelect={setFoto} 
                  currentImage={animal?.fotos?.[0]}
                />
              ) : (
                <div className="view-photo-container">
                  {animal?.fotos?.[0] ? (
                    <img src={animal.fotos[0]} alt={form.nombre} className="view-photo" />
                  ) : (
                    <div className="view-emoji-box" style={{ background: animal?.colorBg || '#f5f5f5' }}>
                      <span className="view-emoji">{animal?.icono || '🐾'}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <div className="modal-right">
              {puedoEditar || esCreacion ? (
                <>
                  <div className="f-row">
                    <div className="f-group">
                      <label className="f-lbl">Nombre</label>
                      <input className="f-inp" name="nombre" placeholder="Nombre..." value={form.nombre} onChange={handle} required />
                    </div>
                    <div className="f-group">
                      <label className="f-lbl">Especie</label>
                      <select className="f-inp" name="especie" value={form.especie} onChange={handle}>
                        <option value="perro">Perro 🐕</option>
                        <option value="gato">Gato 🐈</option>
                        <option value="otro">Otro 🐾</option>
                      </select>
                    </div>
                  </div>

                  <div className="f-row">
                    <div className="f-group">
                      <label className="f-lbl">Edad</label>
                      <input className="f-inp" name="edad" placeholder="Ej: 2 años" value={form.edad} onChange={handle} required />
                    </div>
                    <div className="f-group">
                      <label className="f-lbl">Tamaño</label>
                      <select className="f-inp" name="tamano" value={form.tamano} onChange={handle}>
                        <option value="pequeño">Pequeño</option>
                        <option value="mediano">Mediano</option>
                        <option value="grande">Grande</option>
                      </select>
                    </div>
                  </div>

                  <div className="f-group">
                    <label className="f-lbl">Ubicación</label>
                    <input className="f-inp" name="ubicacion" placeholder="Ej: Campus Central" value={form.ubicacion} onChange={handle} />
                  </div>

                  <div className="f-row">
                    <div className="f-group">
                      <label className="f-lbl">Estado</label>
                      <select className="f-inp" name="estado" value={form.estado} onChange={handle}>
                        <option value="reportado">📢 Reportado</option>
                        <option value="rescate">🦺 Rescatado</option>
                        <option value="tratamiento">🏥 En tratamiento</option>
                        <option value="adopcion">🏠 En adopción</option>
                        <option value="adoptado">🎒 Adoptado</option>
                      </select>
                    </div>
                    <div className="f-group">
                      <label className="f-lbl">Sexo</label>
                      <select className="f-inp" name="sexo" value={form.sexo} onChange={handle}>
                        <option value="desconocido">Desconocido 🐾</option>
                        <option value="macho">Macho ♂️</option>
                        <option value="hembra">Hembra ♀️</option>
                      </select>
                    </div>
                  </div>

                  <div className="f-group">
                    <label className="f-lbl">Historia / descripción</label>
                    <textarea className="f-inp min-h-[100px] resize-y" name="historia" placeholder="Cuenta su historia..." value={form.historia} onChange={handle} />
                  </div>
                </>
              ) : (
                <div className="view-mode-details">
                   <div className="view-item">
                      <label>Especie</label>
                      <p>{form.especie === 'perro' ? 'Perro 🐕' : (form.especie === 'gato' ? 'Gato 🐈' : 'Otro 🐾')}</p>
                   </div>
                   <div className="view-row">
                      <div className="view-item">
                        <label>Edad</label>
                        <p>{form.edad}</p>
                      </div>
                      <div className="view-item">
                        <label>Tamaño</label>
                        <p className="capitalize">{form.tamano}</p>
                      </div>
                   </div>
                   {form.ubicacion && (
                     <div className="view-item">
                       <label>Ubicación</label>
                       <p>📍 {form.ubicacion}</p>
                     </div>
                   )}
                   <div className="view-item">
                     <label>Sexo</label>
                     <p className="capitalize">🧬 {form.sexo}</p>
                   </div>
                   <div className="view-item">
                      <label>Historia</label>
                      <p className="view-historia">{form.historia || 'Sin historia disponible.'}</p>
                   </div>
                </div>
              )}
            </div>
          </div>

          <div className="modal-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose} disabled={loading}>
              {puedoEditar || esCreacion ? 'Cancelar' : 'Cerrar'}
            </button>
            {(puedoEditar || esCreacion) && (
              <button type="submit" className="btn btn-v btn-lg" disabled={loading}>
                {loading ? 'Guardando...' : (esCreacion ? '🐾 Registrar animal' : '✅ Guardar cambios')}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
